/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import * as R from 'ramda';
import { Rectangle } from './position/rectangle';
import { getRect, translatePercentFor, xOffsetFor, yOffsetFor } from '../language/domElement';
import { HorizontalPosition } from './position/horizontalPosition';
import { VerticalPosition } from './position/verticalPosition';
import * as ReactDOM from 'react-dom';
import { LAYER } from './zIndex';
import { FadeShort } from '../styles/animations';
import { assoc } from '../language/module';


/**
 * FloatingContent: a component that renders a position absolute element relative to another element. Includes options
 * to place the floating element in one of the 9 squares of a 3x3 grid centered on the normal element.
 *
 * Be wary: this will be cut off by overflow styles! Not much we can do about it, but the position: fixed fix sorta
 * works... Offset calculations become pretty strange though -- perhaps something for a future denizen of the code to
 * explore.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class FloatingContent extends React.PureComponent<{
  showFloatingContent: boolean;
  xPosition: HorizontalPosition;
  yPosition: VerticalPosition;
  trackMouse: boolean;
  rootCss?: SerializedStyles;
  zIndex?: number;
  onClickOutside: () => void;
  onHoverInside?: () => void;
  onHoverOutside?: () => void;
  children: React.ReactNode;
  floatingContent: React.ReactNode;
}, {
  hasShownFloatingContent: boolean;
  contentRect: Rectangle;
}> {
  private floatingContent: HTMLDivElement | null;
  private floatingContentRef = ref => this.floatingContent = ref;

  private content: HTMLDivElement | null;
  private contentRef = ref => this.content = ref;

  public static defaultProps = {
    trackMouse: false,
  };


  constructor(props) {
    super(props);

    this.state = {
      hasShownFloatingContent: props.showFloatingContent,
      contentRect:             {
        x:      0,
        y:      0,
        width:  0,
        height: 0,
      },
    };
  }

  public componentDidMount() {
    if (this.props.showFloatingContent) {
      this.addDocumentListeners();
    }
    this.setState(prev => assoc('contentRect', getRect(this.content), prev));
  } 

  
  public componentWillUnmount = () => this.removeDocumentListeners();


  public componentDidUpdate(prevProps, prevState) {
    const newRect = getRect(this.content);

    if (!R.equals(this.state.contentRect, newRect)) {
      this.setState(prev => assoc('contentRect', newRect, prev));
    }

      // First time floating content is shown
    if (!prevState.hasShownFloatingContent && this.props.showFloatingContent) {
      this.setState(prev =>
        assoc('hasShownFloatingContent', true, prev)
      );
    }

    // Floating content just opened
    if (this.props.showFloatingContent && !prevProps.showFloatingContent) {
      this.addDocumentListeners();
    }

    // Floating content just closed
    if (!this.props.showFloatingContent && prevProps.showFloatingContent) {
      this.removeDocumentListeners();
    }
  }


  private OnDomTransform = () => this.setState(prev => assoc('contentRect', getRect(this.content), prev));


  private onDocumentClick = event => {
    if (this.floatingContent && !this.floatingContent.contains(event.target)) {
      this.props.onClickOutside();
    }
  };


  private onMouseEnter = () => {
    if (!this.props.trackMouse || !this.props.onHoverInside) {
      return;
    }

    this.props.onHoverInside();
  };


  private onMouseLeave = () => {
    if (!this.props.trackMouse || !this.props.onHoverOutside) {
      return;
    }

    this.props.onHoverOutside();
  };


  private addDocumentListeners = () => {
    document.addEventListener('click', this.onDocumentClick); // Idempotent
    window.addEventListener('scroll', this.OnDomTransform, true); // Idempotent
    window.addEventListener('resize', this.OnDomTransform, true); // Idempotent
  };


  private removeDocumentListeners = () => {
    document.removeEventListener('click', this.onDocumentClick); // Idempotent
    window.removeEventListener('scroll', this.OnDomTransform); // Idempotent
    window.removeEventListener('resize', this.OnDomTransform); // Idempotent
  };


  public render = () => {
    const contentStyle = css`
      display: inline-block; /* Size to content instead of filling available width */
      box-sizing: border-box;
      ${this.props.rootCss};
    `;

    const floatingContentStyle = css`
      position: fixed;
      /* 
        To prevent the fadeOut animation from applying on component mount, we start with display none -- unless we want 
        to be displaying content, in which case it is allowed to fade in. 
      */
      pointer-events: ${this.props.showFloatingContent ? 'all' : 'none'};
      transform: translateX(${translatePercentFor(this.props.xPosition)})
                 translateY(${translatePercentFor(this.props.yPosition)});
      ${xOffsetFor(this.props.xPosition, this.state.contentRect)};
      ${yOffsetFor(this.props.yPosition, this.state.contentRect)};
      z-index: ${this.props.zIndex || LAYER.THREE.FLOATING_CONTENT};
    `;

    const tooltip = ReactDOM.createPortal(
      <FadeShort in={this.props.showFloatingContent}>
        <div ref={this.floatingContentRef} css={floatingContentStyle}>
          {this.props.floatingContent}
        </div>
      </FadeShort>,
      document.body,
    );

    return this.props.trackMouse
      // With mouse listeners
           ? <div ref={this.contentRef}
                  onMouseEnter={this.onMouseEnter}
                  onMouseLeave={this.onMouseLeave}
                  css={contentStyle}>
             <div css={css`pointer-events: ${this.props.showFloatingContent ? 'none' : 'all'};`}>
               {this.props.children}
             </div>
             {tooltip}
           </div>
      // Without mouse listeners
           : <div ref={this.contentRef} css={contentStyle}>
             {this.props.children}
             {tooltip}
           </div>;
  };
};
